import React, { useEffect, useRef, useState } from "react";
import { BRAND } from "../mock";
import { Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const CALENDLY_URL = "https://calendly.com/paulvillasboas/30min";

function setSeoMeta() {
  document.title = "Book a Call | Paul Vilas";
  const setMeta = (name, content, attr = "name") => {
    let tag = document.querySelector(`meta[${attr}="${name}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute(attr, name);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };
  setMeta("description", "Book your 1-on-1 call with Paul Vilas.");
  setMeta("robots", "noindex, follow");
}

export default function Schedule() {
  const embedRef = useRef(null);
  const [calendlyReady, setCalendlyReady] = useState(
    () =>
      typeof document !== "undefined" &&
      !!document.getElementById("calendly-widget-script"),
  );

  useEffect(() => {
    setSeoMeta();

    // Inject Calendly assets only once
    const widgetId = "calendly-widget-script";
    const cssId = "calendly-widget-css";

    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }

    const onScriptReady = () => setCalendlyReady(true);

    if (!document.getElementById(widgetId)) {
      const script = document.createElement("script");
      script.id = widgetId;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.onload = onScriptReady;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div className="dark-site min-h-screen bg-soft flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto flex justify-center">
          <BrandLogo variant="yellow" className="h-9 md:h-11" />
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-10 md:pt-16 pb-8 md:pb-10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-center">
          {/* Text */}
          <div className="text-center md:text-left order-2 md:order-1">
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-paul-yellow font-bold mb-4">
              Inquiry Received
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-navy leading-tight">
              Next, book a date and time for us to chat
            </h1>
            <p className="mt-5 text-base md:text-lg text-gray-600 max-w-xl mx-auto md:mx-0 leading-relaxed">
              If you don&apos;t see a date/time that works for you, feel free to
              reply back to the email I just sent.
            </p>
          </div>

          {/* Producer photo */}
          <div className="flex justify-center md:justify-end order-1 md:order-2">
            <img
              src="https://customer-assets.emergentagent.com/job_producer-vilas-beats/artifacts/2xyeiq8z_Paul_vilas_pic.JPG"
              alt="Paul Vilas"
              className="w-40 h-52 md:w-52 md:h-64 object-cover rounded-xl shadow-[0_10px_40px_rgba(11,21,53,0.15)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Calendly inline embed */}
      <section className="flex-1 px-3 md:px-6 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-[0_10px_40px_rgba(11,21,53,0.06)] border border-gray-100 overflow-hidden relative">
          {!calendlyReady && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white">
              <div className="flex items-center gap-3 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">
                  Loading available times…
                </span>
              </div>
            </div>
          )}
          <div
            ref={embedRef}
            className="calendly-inline-widget"
            data-url={CALENDLY_URL}
            style={{ minWidth: "320px", height: "780px" }}
          />
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Calendar shows times in your local timezone automatically. You can
          change it from the dropdown at the top of the calendar.
        </p>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
